import { useRouter } from '@tanstack/react-router';
import { useStrictCurrentEnterpriseId } from '@mews-commander/core';
import { Breadcrumbs, GuideLinkButton, useBreadcrumbs } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { MY_SUBSCRIPTIONS_CATEGORY_ID } from '../../constants';
import { useTargetEnterpriseIdContext } from '../../context';
import { DeleteButton, DeleteButtonButtonProps } from '../DeleteButton';
import { IntegrationSettingsBaseLayout } from './IntegrationSettingsBaseLayout';

interface IntegrationSettingsUpdateLayoutProps extends Pick<DeleteButtonButtonProps, 'deleteAction' | 'entityName'> {
    guideLink?: string;
}

export const IntegrationSettingsUpdateLayout = ({
    entityName,
    deleteAction,
    guideLink,
    children,
}: React.PropsWithChildren<IntegrationSettingsUpdateLayoutProps>) => {
    const { buildLocation } = useRouter();
    const breadcrumbs = useBreadcrumbs();
    const strictCurrentEnterpriseId = useStrictCurrentEnterpriseId();
    const targetEnterpriseId = useTargetEnterpriseIdContext();

    const title = lt(TextKey.MySubscriptionPlural);
    const path = buildLocation({
        to: '/$currentEnterpriseId/EnterpriseIntegration/Marketplace',
        params: { currentEnterpriseId: strictCurrentEnterpriseId },
        search: {
            OwnerId: targetEnterpriseId,
            CategoryId: MY_SUBSCRIPTIONS_CATEGORY_ID,
        },
    }).href;

    const integrationBreadcrumb: React.ComponentProps<typeof Breadcrumbs>['breadcrumbs'] = [
        {
            title,
            path,
        },
    ];

    const breadcrumbsWithIntegration = integrationBreadcrumb.concat(breadcrumbs);

    const guideLinkComponent = guideLink ? <GuideLinkButton guideLink={guideLink} /> : null;
    const actions = (
        <>
            <DeleteButton entityName={entityName} deleteAction={deleteAction} />
            {guideLinkComponent}
        </>
    );

    return (
        <IntegrationSettingsBaseLayout breadcrumbs={breadcrumbsWithIntegration} actions={actions}>
            {children}
        </IntegrationSettingsBaseLayout>
    );
};
